import React, { useEffect, useState, useCallback } from "react";
import { X, ArrowRight, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";

const SHOWN_KEY = "md_newsletter_popup_v1";
const DELAY_MS = 20000; // 20 sec on site before first appearance
const PROMO_CODE = "BIENVENUE";

const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const markSeen = () => {
    try { localStorage.setItem(SHOWN_KEY, String(Date.now())); } catch { /* noop */ }
  };

  const close = useCallback(() => {
    setOpen(false);
    markSeen();
  }, []);

  useEffect(() => {
    // Already seen → never show again
    try {
      if (localStorage.getItem(SHOWN_KEY)) return;
    } catch { /* noop */ }

    // Delay trigger
    const delayTimer = setTimeout(() => setOpen(true), DELAY_MS);

    // Exit-intent trigger (desktop only)
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) {
        setOpen(true);
      }
    };
    document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearTimeout(delayTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      setSuccess(true);
      markSeen();
    } catch {
      toast.error("Adresse email invalide.");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      toast.success("Code copié");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="newsletter-popup-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={close}
    >
      <div
        data-testid="newsletter-popup"
        className="relative w-full max-w-[520px] bg-[#0A0A0A] border border-[color:var(--gold)]/40 shadow-[0_25px_80px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="newsletter-popup-close"
          onClick={close}
          className="absolute top-4 right-4 text-white/60 hover:text-[color:var(--gold)] transition-colors"
          aria-label="Fermer"
        >
          <X size={20} strokeWidth={1.2} />
        </button>

        <div className="px-8 py-12 md:px-12 md:py-14 text-center">
          {!success ? (
            <>
              <p className="label-caps text-[color:var(--gold)] mb-6 text-[10px]">Exclusivité · Le Cercle Douba</p>
              <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight mb-5">
                Recevez <em className="italic text-[color:var(--gold-soft)]">−15%</em><br />
                sur votre première commande
              </h3>
              <p className="text-white/60 text-sm font-light mb-8 leading-relaxed">
                Rejoignez notre cercle privé. Nouveautés en avant-première,
                confidences olfactives et offres réservées.
              </p>
              <form onSubmit={submit} className="flex flex-col gap-4">
                <input
                  data-testid="newsletter-popup-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre adresse email"
                  className="input-line text-center"
                  autoFocus
                />
                <button
                  data-testid="newsletter-popup-submit"
                  type="submit"
                  disabled={loading}
                  className="btn-gold justify-center"
                >
                  {loading ? "..." : "Recevoir mon code"}
                  {!loading && <ArrowRight size={14} />}
                </button>
              </form>
              <button
                data-testid="newsletter-popup-dismiss"
                onClick={close}
                className="mt-6 text-white/35 hover:text-white/60 text-xs label-caps transition-colors"
              >
                Non merci, je préfère payer plein tarif
              </button>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-14 h-14 rounded-full border border-[color:var(--gold)] flex items-center justify-center">
                  <Check size={26} className="text-[color:var(--gold)]" strokeWidth={1.5} />
                </div>
              </div>
              <p className="label-caps text-[color:var(--gold)] mb-5 text-[10px]">Bienvenue dans la Maison</p>
              <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight mb-6">
                Votre code <em className="italic text-[color:var(--gold-soft)]">−15%</em>
              </h3>
              <button
                data-testid="newsletter-popup-copy-code"
                onClick={copyCode}
                className="mx-auto mb-6 flex items-center gap-3 px-6 py-4 border border-[color:var(--gold)] text-[color:var(--gold)] label-caps text-sm tracking-[0.3em] hover:bg-[color:var(--gold)]/10 transition-colors"
              >
                {PROMO_CODE}
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
              <p className="text-white/50 text-sm font-light mb-8">
                Valable sur votre première commande. À utiliser au moment du paiement.
              </p>
              <button
                data-testid="newsletter-popup-continue"
                onClick={close}
                className="btn-gold w-full justify-center"
              >
                Découvrir la collection <ArrowRight size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
